import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { MediaTypeBadges, StatusBadge } from "@/components/Badges";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Lock, ExternalLink, ArrowRight, Search, Download as DownloadIcon, RotateCcw } from "lucide-react";

const TYPES = [{ v: "", l: "All types" }, { v: "photos", l: "Photos" }, { v: "video", l: "Video" }, { v: "download", l: "Downloads" }];

export default function MyDownloads() {
  const { user } = useAuth();
  const [media, setMedia] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [eventId, setEventId] = useState("");
  const [unlock, setUnlock] = useState(null);
  const [requesting, setRequesting] = useState({});

  const load = () => api.get("/my/media").then(({ data }) => setMedia(data)).catch(() => setMedia([]));
  useEffect(() => { load(); }, []);

  const events = useMemo(() => {
    const m = {};
    (media || []).forEach((x) => { if (x.event_id) m[x.event_id] = x.event_name || "Untitled"; });
    return Object.entries(m);
  }, [media]);

  const filtered = (media || []).filter((m) => {
    if (q && !`${m.title} ${m.event_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (type && !(m.media_types || []).includes(type)) return false;
    if (eventId && m.event_id !== eventId) return false;
    return true;
  });
  const available = filtered.filter((m) => !m.expired);
  const expired = filtered.filter((m) => m.expired);

  const requestLink = async (m) => {
    setRequesting((r) => ({ ...r, [m.id]: true }));
    try { await api.post(`/my/media/${m.id}/request-link`); toast.success("We've emailed you a fresh download link."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRequesting((r) => ({ ...r, [m.id]: false })); }
  };

  const Card = ({ m, isExpired }) => (
    <div key={m.grant_id} data-testid={`download-item-${m.id}`} className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/70">
      <div className="relative aspect-[16/10] bg-zinc-950">
        {m.thumbnail_url && <img src={m.thumbnail_url} alt={m.title} className={`h-full w-full object-cover ${isExpired ? "opacity-50" : ""}`} />}
        <div className="absolute left-3 top-3">{isExpired ? <span data-testid={`expired-badge-${m.id}`} className="inline-flex rounded-full border border-rose-500/40 bg-rose-500/20 px-2.5 py-0.5 text-xs font-semibold text-rose-300">Expired</span> : <StatusBadge status={m.order_status} />}</div>
      </div>
      <div className="space-y-3 p-5">
        {m.event_name && <div className="text-xs font-medium text-zinc-500">{m.event_name}</div>}
        <MediaTypeBadges types={m.media_types} testidPrefix={`dl-badge-${m.id}`} />
        <h3 className="font-display text-lg font-bold text-white">{m.title}</h3>
        {isExpired ? (
          <Button data-testid={`request-link-${m.id}`} onClick={() => requestLink(m)} disabled={requesting[m.id]} className="w-full bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
            {requesting[m.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RotateCcw className="mr-1.5 h-4 w-4" /> Request download link</>}
          </Button>
        ) : (
          <div className="space-y-2 pt-1">
            {!m.available ? <p className="text-sm text-zinc-500">Not currently available.</p>
              : m.links.length === 0 ? <p className="text-sm text-zinc-500">Links coming soon.</p>
              : m.links.map((l) => (
                <button key={l.id} data-testid={`unlock-link-${m.id}-${l.id}`} onClick={() => setUnlock({ media: m, link: l })}
                  className="flex w-full items-center justify-between rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-2 text-left text-sm text-zinc-200 hover:border-rose-500/50">
                  <span className="flex items-center gap-2">{l.has_password ? <Lock className="h-3.5 w-3.5 text-amber-400" /> : <ExternalLink className="h-3.5 w-3.5 text-cyan-400" />}{l.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
                </button>
              ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <Navbar />
      <Breadcrumb label="Back to store" to="/" />
      <div className="mx-auto max-w-7xl space-y-8 px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">{user?.name}</div>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">My Downloads</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-800 bg-[#0d0d10] p-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
            <Input data-testid="downloads-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your media…" className="h-9 border-zinc-700 bg-zinc-950 pl-9 text-sm text-white" />
          </div>
          <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="downloads-type" className="h-9 w-40 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900 text-white">{TYPES.map((t) => <SelectItem key={t.v || "all"} value={t.v || "all"}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={eventId || "all"} onValueChange={(v) => setEventId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="downloads-event" className="h-9 w-48 border-zinc-700 bg-zinc-950 text-sm text-white"><SelectValue placeholder="All events" /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-zinc-900 text-white">
              <SelectItem value="all">All events</SelectItem>
              {events.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {media === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#ff2a5f]" /></div>
        ) : (
          <>
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold uppercase tracking-tight text-white"><DownloadIcon className="h-5 w-5 text-[#ff2a5f]" /> Available media</h2>
              {available.length === 0 ? <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 py-12 text-center text-zinc-500">No available media match your filters.</div>
                : <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{available.map((m) => <Card key={m.grant_id} m={m} isExpired={false} />)}</div>}
            </section>
            {expired.length > 0 && (
              <section data-testid="expired-section">
                <h2 className="mb-4 font-display text-xl font-bold uppercase tracking-tight text-rose-400">Expired media</h2>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{expired.map((m) => <Card key={m.grant_id} m={m} isExpired={true} />)}</div>
              </section>
            )}
          </>
        )}
      </div>
      <UnlockDialog data={unlock} onClose={() => setUnlock(null)} />
      <Footer />
    </div>
  );
}

function UnlockDialog({ data, onClose }) {
  const [pw, setPw] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setRes(null); }, [data]);
  if (!data) return null;
  const { media, link } = data;
  const go = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/my/media/${media.id}/links/${link.id}/unlock`, { password: pw });
      setRes(r); window.open(r.url, "_blank");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display">{link.label}</DialogTitle><DialogDescription className="text-zinc-500">{media.title}</DialogDescription></DialogHeader>
        {link.has_password && !res && (
          <Input data-testid="unlock-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Enter link password" className="border-zinc-700 bg-zinc-950 text-white" />
        )}
        {res ? <a data-testid="open-download" href={res.url} target="_blank" rel="noreferrer" className="block rounded-lg bg-[#ff2a5f] px-4 py-2 text-center font-semibold text-white">Open download</a>
          : <Button data-testid="confirm-unlock" onClick={go} disabled={busy} className="bg-[#ff2a5f] text-white hover:bg-[#e01f50]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open"}</Button>}
      </DialogContent>
    </Dialog>
  );
}
